import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperIcon,
  MatStepperNext,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FirmendatenComponent } from './firmendaten/firmendaten.component';
import { ZusammenfassungComponent } from './zusammenfassung/zusammenfassung.component';
import { BenutzerdatenComponent } from './benutzerdaten/benutzerdaten.component';
import { ZugangsdatenComponent } from './zugangsdaten/zugangsdaten.component';
import { LastschriftComponent } from './lastschrift/lastschrift.component';
import { MarktplatzBedingungenComponent } from './marktplatz-bedingungen/marktplatz-bedingungen.component';
import { BENUTZERNAME, EMAIL, PLZ, STEUERNUMMER, TELEFON } from '../validierungen';
import { RegistrierungService } from '../services/registrierung.service';
import { PasswordStrengthService } from '../services/password-strength.service';
import { TranslatedNotificationService } from '../services/translated-notification.service';
import { ActivatedRoute } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { MitarbeiterResponse } from '../generated-model/mitarbeiterResponse';
import { PrueferResponse } from '../generated-model/prueferResponse';
import { AgbComponent } from './agb/agb.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-registrierung',
  imports: [
    MatStepper,
    MatStep,
    ReactiveFormsModule,
    MatInputModule,
    MatStepLabel,
    MatButton,
    MatStepperNext,
    MatStepperPrevious,
    MatStepperIcon,
    MatIcon,
    FirmendatenComponent,
    ZusammenfassungComponent,
    BenutzerdatenComponent,
    ZugangsdatenComponent,
    LastschriftComponent,
    MarktplatzBedingungenComponent,
    NgOptimizedImage,
    AgbComponent,
    TranslatePipe,
  ],
  templateUrl: './registrierung.component.html',
  styleUrl: './registrierung.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistrierungComponent implements OnInit, OnDestroy {
  uuid: string | null = null;
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  private readonly _formBuilder = inject(FormBuilder);

  private readonly registrierungService = inject(RegistrierungService);
  private readonly passwordStrengthService = inject(PasswordStrengthService);
  protected msg = inject(TranslatedNotificationService);

  private readonly destroy$ = new Subject<void>();

  zugangsdatenComponent = viewChild(ZugangsdatenComponent);
  @ViewChild(MatStepper) stepper!: MatStepper;

  constructor() {
    // Monitor password strength from zugangsdaten component and update form validity
    effect(() => {
      const zugangsdatenComponent = this.zugangsdatenComponent();
      if (zugangsdatenComponent) {
        this.passwortStaerke.set(zugangsdatenComponent.passwortstaerke());
        this.updateFormValidityCache();
      }
    });
  }

  aareonUser = signal<boolean>(false);
  testFirma = signal<boolean>(false);
  testUmgebung = signal<boolean>(false);
  simuliereProduktion = signal<boolean>(true);
  registrierungOhneMarktPlatzRelation = signal<boolean>(false);

  adressPruefung = signal<boolean>(false);
  registrierungAbgeschlossen = signal<boolean>(false);
  registrierungEmail = signal<string>('');
  abschlussRegistrierungOK = signal<boolean>(false);
  isMitarbeiterRegistrierung = signal<boolean>(false);
  isPrueferRegistrierung = signal<boolean>(false);
  isUUIDGueltig = signal<boolean>(false);

  // Cache form validity to avoid repeated method calls
  benutzerDatenValidCache = signal<boolean>(false);
  firmenDatenValidCache = signal<boolean>(false);
  passwortStaerke = signal<number>(0);

  ngOnInit(): void {
    localStorage.clear();
    this.uuid = this.route.snapshot.paramMap.get('uuid');
    const registrierungsTyp = this.route.snapshot.url[1]?.path;
    this.isMitarbeiterRegistrierung.set(registrierungsTyp === 'mitarbeiter');
    this.isPrueferRegistrierung.set(registrierungsTyp === 'pruefer');

    const host = window.location.hostname.toLowerCase();
    let isTest = host.includes('localhost') || host.includes('aswtest') || host.includes('127.0.0.1');
    this.testUmgebung.set(isTest);
    // Setup form validity change monitoring
    this.setupFormValidityMonitoring();

    if (this.isMitarbeiterRegistrierung()) {
      this.registrierungService.getRegistrierungsDatenMitarbeiter(this.uuid!).subscribe({
        next: (response) => {
          if (response.success) {
            this.prepareFormulareMitarbeiterRegistrierung(response);
            this.isUUIDGueltig.set(true);
          } else {
            this.msg.showErrorMessage('Fehler:' + response.message);
          }
        },
        error: (e) => {
          this.msg.showErrorMessage('technischer Fehler:' + e.message);
        },
        complete: () => console.info('complete'),
      });
    } else if (this.isPrueferRegistrierung()) {
      this.registrierungService.getRegistrierungsDatenPruefer(this.uuid!).subscribe({
        next: (response) => {
          if (response.success) {
            this.preparePrueferRegistrierung(response);
            this.isUUIDGueltig.set(true);
          } else {
            this.msg.showErrorMessage('Fehler:' + response.message);
          }
        },
        error: (e) => {
          this.msg.showErrorMessage('technischer Fehler:' + e.message);
        },
        complete: () => console.info('complete'),
      });
    }
  }

  private setupFormValidityMonitoring(): void {
    // Monitor firmendaten form validity
    this.formGroupFirmendaten.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateFormValidityCache();
    });

    // Monitor benutzer form validity
    this.formGroupBenutzer.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateFormValidityCache();
    });

    // Monitor zugangsdaten form validity
    this.formGroupZugangsdaten.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateFormValidityCache();
    });

    // Initial update
    this.updateFormValidityCache();
  }

  updateFormValidityCache(): void {
    this.firmenDatenValidCache.set(this.formGroupFirmendaten.valid && this.adressPruefung());
    const zugangsdatenComponent = this.zugangsdatenComponent();
    const passwordRuleVerified = !!zugangsdatenComponent && zugangsdatenComponent.passwortstaerke() === 5;
    this.benutzerDatenValidCache.set(
      this.formGroupBenutzer.valid && this.formGroupZugangsdaten.valid && passwordRuleVerified
    );
  }

  private static readonly AAREON_DOMAIN_MARKER = ['aareon', 'mareon', 'wohnungshelden'];

  updateAareonUser(email: string | null | undefined): void {
    const domain = email?.split('@')[1]?.toLowerCase() ?? '';
    this.aareonUser.set(RegistrierungComponent.AAREON_DOMAIN_MARKER.some((marker) => domain.includes(marker)));
    this.behandleSetzenMarktplatzRelation();
  }

  updateTestFirma(firmenname: string | null | undefined): void {
    this.testFirma.set(firmenname?.toUpperCase().includes('@TEST@') ?? false);
    this.behandleSetzenMarktplatzRelation();
  }

  private behandleSetzenMarktplatzRelation(): void {
    const firmendaten = this.registrierungService.registrierungsdaten?.firmendaten;
    if (firmendaten) {
      const isTestUmgebungMitAktivierterSimulation = this.simuliereProduktion() && this.testUmgebung();
      const isProduktion = !this.testUmgebung() && !this.simuliereProduktion();
      let marktPlatzRelationVerhindern: boolean =
        (isTestUmgebungMitAktivierterSimulation || isProduktion) && (this.testFirma() || this.aareonUser());
      this.registrierungOhneMarktPlatzRelation.set(marktPlatzRelationVerhindern);
      firmendaten.test = this.registrierungOhneMarktPlatzRelation();
    }
  }

  updateProduktionSimulation(simulation: boolean): void {
    this.simuliereProduktion.set(simulation);
    this.behandleSetzenMarktplatzRelation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private prepareFormulareMitarbeiterRegistrierung(response: MitarbeiterResponse) {
    // Initialize test property if undefined
    if (response.firmendaten && response.firmendaten.test === undefined) {
      response.firmendaten.test = false;
    }
    this.registrierungService.registrierungsdaten.firmendaten = response.firmendaten;
    this.registrierungService.registrierungsdaten.benutzerdaten = response.personenDaten;
    this.registrierungService.registrierungsdaten.zugangsdaten = response.zugangsdaten;
    this.updateAareonUser(response.personenDaten?.email);
    this.formGroupBenutzer.patchValue({
      vorname: response.personenDaten?.vorname,
      nachname: response.personenDaten?.nachname,
      telefon: response.personenDaten?.telefon,
      email: response.personenDaten?.email,
      email_wdh: response.personenDaten?.email,
    });

    this.formGroupZugangsdaten.patchValue({
      benutzername: response.zugangsdaten?.benutzername,
    });
  }

  private preparePrueferRegistrierung(response: PrueferResponse) {
    // Beim Pruefer ist nur die Firma (Auftraggeber-Einladung) bekannt, keine konkrete Person -
    // vorname/nachname/email/benutzername bleiben zur Eingabe durch den Nutzer frei.
    if (response.firmendaten && response.firmendaten.test === undefined) {
      response.firmendaten.test = false;
    }
    this.registrierungService.registrierungsdaten.firmendaten = response.firmendaten;
    this.registrierungService.registrierungsdaten.benutzerdaten = response.personenDaten;
    this.registrierungService.registrierungsdaten.zugangsdaten = response.zugangsdaten;
    this.formGroupBenutzer.patchValue({
      strasse: response.personenDaten?.strasse,
      hausnummer: response.personenDaten?.hausnummer,
      plz: response.personenDaten?.plz,
      ort: response.personenDaten?.ort,
    });
  }

  formGroupBenutzer = this._formBuilder.group({
    anrede: [''],
    titel: [''],
    vorname: ['', Validators.required],
    nachname: ['', Validators.required],
    strasse: [''],
    hausnummer: [''],
    plz: [''],
    ort: [''],
    land: ['Deutschland'],
    email: ['', [Validators.required, Validators.pattern(EMAIL)]],
    email_wdh: ['', [Validators.required, Validators.pattern(EMAIL)]],
    telefon: ['', [Validators.required, Validators.pattern(TELEFON)]],
  });

  formGroupZugangsdaten = this._formBuilder.group({
    benutzername: ['', [Validators.required, Validators.pattern(BENUTZERNAME)]],
    passwort: ['', [Validators.required, Validators.minLength(10), this.passwordStrengthValidator.bind(this)]],
    passwort_wdh: ['', [Validators.minLength(10)]],
    geheime_frage: [''],
    geheime_antwort: [''],
  });

  formGroupFirmendaten = this._formBuilder.group({
    firma: ['', Validators.required],
    name: [''],
    strasse: ['', Validators.required],
    hausnummer: [''],
    plz: ['', [Validators.required, Validators.pattern(PLZ)]],
    ort: ['', Validators.required],
    land: ['Deutschland', Validators.required],
    telefon: ['', [Validators.required, Validators.pattern(TELEFON)]],
    mobil: [''],
    steuernummer: ['', [Validators.required, Validators.pattern(STEUERNUMMER)]],
    ustId: [''],
    handelsregister: [''],
    homepage: [''],
    simuliereProd: [false],
  });

  formZusammenfassung = this._formBuilder.group({
    vertragsbestimmungen: [true],
    vertragsabschluss: [true],
  });

  formGroupLastschrift = this._formBuilder.group({
    kontoinhaber: ['', Validators.required],
    kreditinstitut: [{ value: '', disabled: true }, Validators.required],
    iban: ['', [Validators.required, Validators.pattern('^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$')]],
    bic: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('^[A-Z]{6}[A-Z0-9]{2,5}$')]],
    lastschrift_erlaubt: [true, Validators.requiredTrue],
    einverstanden_vertrag: [false, Validators.requiredTrue],
    berechtigung_stellvertretung: [false],
  });

  formGroupMarktplatzbedingungen = this._formBuilder.group({
    einverstanden: [false, Validators.requiredTrue],
    km_bereich: [0, [Validators.required]],
  });

  formGroupAGB = this._formBuilder.group({
    einverstanden_agb: [false, Validators.requiredTrue],
  });

  weiterFirmendaten(): void {
    if (this.firmenDatenValidCache()) {
      this.stepper.next();
      return;
    }
    this.formGroupFirmendaten.markAllAsTouched();
  }

  weiterBenutzerdaten(): void {
    if (this.benutzerDatenValidCache()) {
      this.stepper.next();
      return;
    }
    this.formGroupBenutzer.markAllAsTouched();
    this.formGroupZugangsdaten.markAllAsTouched();
  }

  registriereBenutzer() {
    this.registrierungService.createVertrag().subscribe({
      next: (response) => {
        if (response.success) {
          this.msg.showSucessMessage('Anlage erfolgreich!, Vertragsnummer: ' + response.vertragNummer);
          this.registrierungAbgeschlossen.set(true);
        } else {
          this.msg.showErrorMessage('Die Registrierung ist fehlgeschlagen: ' + response.message);
          this.registrierungAbgeschlossen.set(false);
        }
      },
      error: () => {
        this.msg.showErrorMessage('Bei der Anlage des Vertrages ist ein technischer Fehler aufgetreten.');
      },
      complete: () => console.info('complete'),
    });
  }

  registriereMitarbeiter() {
    this.registrierungService.registrierungsdaten.uuid_mitarbeiter = this.uuid!;
    this.registrierungService.registriereMitarbeiter().subscribe({
      next: (response) => {
        if (response.success) {
          this.msg.showSucessMessage('Die Registrierung war erfolgreich');
          this.registrierungAbgeschlossen.set(true);
        } else {
          this.msg.showErrorMessage('Die Registrierung ist fehlgeschlagen: ' + response.message);
          this.registrierungAbgeschlossen.set(false);
        }
      },
      error: () => {
        this.msg.showErrorMessage('Bei der Anlage des Vertrages ist ein technischer Fehler aufgetreten.');
      },
      complete: () => console.info('complete'),
    });
  }

  registrierePruefer() {
    this.registrierungService.registrierungsdaten.uuid_pruefer = this.uuid!;
    this.registrierungService.registrierePruefer().subscribe({
      next: (response) => {
        if (response.success) {
          this.msg.showSucessMessage('Die Registrierung war erfolgreich');
          this.registrierungAbgeschlossen.set(true);
        } else {
          this.msg.showErrorMessage('Die Registrierung ist fehlgeschlagen: ' + response.message);
          this.registrierungAbgeschlossen.set(false);
        }
      },
      error: () => {
        this.msg.showErrorMessage('Bei der Anlage des Pruefunternehmens ist ein technischer Fehler aufgetreten.');
      },
      complete: () => console.info('complete'),
    });
  }

  getRegistrierungsdaten() {
    return this.registrierungService.registrierungsdaten;
  }

  getEinladendeFirma(): string | undefined {
    return this.getRegistrierungsdaten().firmendaten?.firma;
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;

    // If password is empty, let Validators.required handle it
    if (!password) {
      return null;
    }

    // Use injected service to check password strength
    if (!this.passwordStrengthService.isPasswordStrong(password)) {
      return { passwordStrengthError: true };
    }
    return null;
  }

  onStepChanged(): void {
    // Use setTimeout to ensure DOM is fully updated before scrolling
    // iOS needs extra time to properly handle scroll operations
    setTimeout(() => {
      // Scroll to top with multiple approaches for cross-browser compatibility
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
  }
}
